import { useState, useEffect, useContext } from "react";
import { TextField, Button, Grid, Checkbox, FormControlLabel, Tooltip } from "@mui/material";
import styles from "./styles.module.css";
import { ReactComponent as UploadIcon } from "images/svg/upload.svg";
import { ReactComponent as CloseRoundedIcon } from "images/svg/Close.svg";
import ItemService from "services/ItemService";
import toast from "react-hot-toast";
import Loader from "../../utils/Loading";
import ProductContext from "context/ProductContext";
import KotManager from "helpers/KotManager";

function EditCategoryPopup({ open, onClose, category, onUpdated }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [kitchenName, setKitchenName] = useState("");
  const [kitchenIp, setKitchenIp] = useState("");
  const [availableDays, setAvailableDays] = useState("");
  const [availableTimeRange, setAvailableTimeRange] = useState("");
  const [order, setOrder] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isAvailable, setIsAvailable] = useState(true);
  const [hideFromPos, setHideFromPos] = useState(false);

  const { updateCategory } = useContext(ProductContext);

  useEffect(() => {
    if (category) {
      setName(category.name || "");
      setDescription(category.description || "");
      setKitchenName(category.kitchen_name || null);
      setKitchenIp(category.kitchen_ip || null);
      setAvailableDays(category.available_days || "");
      setAvailableTimeRange(category.available_time_range || "");
      setOrder(category.order || "");
      setPreviewUrl(category.image || category.image_url || "");
      setImageFile(null);
      setIsAvailable(category.is_available ?? true);
      setHideFromPos(category.hide_from_pos ?? false);
    } else {
      setName("");
      setDescription("");
      setKitchenName(null);
      setKitchenIp(null);
      setAvailableDays("");
      setAvailableTimeRange("");
      setOrder("");
      setPreviewUrl("");
      setImageFile(null);
      setHideFromPos(false);
    }
  }, [category]);

  const handleUpdate = async () => {
    if (!name.trim()) {
      toast.error("Enter a name for the category");
      return;
    }

    if (!category?.id) {
      toast.error("Invalid category");
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        name: name.trim(),
        description: description || "",
        kitchen_name: kitchenName || null,
        kitchen_ip: kitchenIp || null,
        available_days: availableDays,
        available_time_range: availableTimeRange,
        order: order ? Number(order) : 1,
        is_available: isAvailable,
      };

      const origOrder = category.order ? Number(category.order) : 1;
      const parsedOrder = order ? Number(order) : 1;

      const hasOtherChanges =
        payload.name !== (category.name || "") ||
        payload.description !== (category.description || "") ||
        payload.kitchen_name !== (category.kitchen_name || null) ||
        payload.kitchen_ip !== (category.kitchen_ip || null) ||
        payload.available_days !== (category.available_days || "") ||
        payload.available_time_range !== (category.available_time_range || "") ||
        parsedOrder !== origOrder ||
        payload.is_available !== (category.is_available ?? true) ||
        Boolean(imageFile);

      let updatedCategory = {
        ...category,
        ...payload,
        hide_from_pos: hideFromPos,
      };

      if (hasOtherChanges) {
        const updatedResp = await ItemService.editItemCategory(payload, category.id);
        const serverData = updatedResp && (updatedResp.data || updatedResp);
        if (serverData && typeof serverData === "object") {
          updatedCategory = {
            ...updatedCategory,
            ...serverData,
            hide_from_pos: hideFromPos,
          };
        }

        if (imageFile) {
          const formData = new FormData();
          formData.append("image", imageFile);
          const imgResp = await ItemService.uploadCategoryImage(formData, category.id);
          if (imgResp && (imgResp.data || imgResp).image) {
            updatedCategory.image = (imgResp.data || imgResp).image;
          }
        }
      }

      updateCategory(updatedCategory);

      onUpdated && onUpdated(updatedCategory);
      onClose && onClose();
    } catch (error) {
      console.error("Failed to update category", error);
      toast.error("Failed to update category");
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestKOT = async () => {
    const trimmedIp = kitchenIp?.trim();
    if (!trimmedIp) {
      toast.error("No Ip for the kitchen");
      return;
    }

    try {
      await KotManager.printTestKot({
        kitchenIp: trimmedIp,
        kitchenName: kitchenName?.trim(),
        categoryName: name,
      });
    } catch (error) {
      console.error("Failed to test KOT", error);
      toast.error(error?.message === "No Ip for the kitchen" ? error.message : "Failed to test KOT");
    }
  };

  const textFieldStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      padding: "0 12px",
      "& fieldset": {
        borderColor: "#CDCDCF",
        borderWidth: "1px",
      },
      "&:hover fieldset": {
        borderColor: "#CDCDCF",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#7c7c7d",
        borderWidth: "1px",
      },
    },
    "& .MuiInputBase-input": {
      height: "40px",
      padding: 0,
      "&::placeholder": {
        color: "#7c7c7d",
        opacity: 1,
        fontSize: "14px",
      },
    },
  };

  if (!open) return null;

  return (
    <div className={styles.popupOverlay}>
      <Loader isOpen={isLoading} />

      <div className={styles.popupContainer}>
        <div className={styles.closeIcoSection}>
          <CloseRoundedIcon onClick={onClose} className={styles.icon} />
        </div>
        <div className={styles.headerSection} style={{ paddingBottom: "15px" }}>
          <div>
            <h2>Edit category</h2>
            <h4>Update category details</h4>
          </div>
        </div>

        <Grid container className={styles.CategoryWrapper}>
          <Grid item xs={12} className={styles.textFieldArea}>
            <TextField
              fullWidth
              placeholder="Category Name"
              value={name || ""}
              onChange={(e) => setName(e.target.value)}
              sx={textFieldStyles}
            />
          </Grid>

          <Grid item xs={12} className={styles.textFieldArea}>
            <TextField
              fullWidth
              placeholder="Kitchen Name"
              value={kitchenName || ""}
              onChange={(e) => setKitchenName(e.target.value)}
              sx={textFieldStyles}
            />
          </Grid>

          <Grid item xs={12} className={styles.textFieldArea}>
            <TextField
              fullWidth
              placeholder="Kitchen IP"
              value={kitchenIp || ""}
              onChange={(e) => setKitchenIp(e.target.value)}
              sx={textFieldStyles}
            />
          </Grid>

          <Grid item xs={12} className={styles.textFieldArea}>
            <TextField
              fullWidth
              type="number"
              placeholder="Display Order"
              value={order || ""}
              onChange={(e) => setOrder(e.target.value)}
              sx={textFieldStyles}
            />
          </Grid>

          <Grid item xs={12} className={styles.textFieldArea}>
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="Description"
              value={description || ""}
              onChange={(e) => setDescription(e.target.value)}
              sx={textFieldStyles}
            />
          </Grid>

          <Grid item xs={12} className={styles.textFieldArea}>
            <Tooltip title="Use the Web Application to edit this field" arrow>
              <div className={styles.readOnlyArea}>
                <div className={styles.readOnlyLabel}>Available Days</div>
                <div className={styles.readOnlyValue}>{availableDays || "Not specified"}</div>
              </div>
            </Tooltip>
          </Grid>

          <Grid item xs={12} className={styles.textFieldArea}>
            <Tooltip title="Use the Web Application to edit this field" arrow>
              <div className={styles.readOnlyArea}>
                <div className={styles.readOnlyLabel}>Available Time Range</div>
                <div className={styles.readOnlyValue}>{availableTimeRange || "Not specified"}</div>
              </div>
            </Tooltip>
          </Grid>

          <Grid item xs={12}>
            <div>
              {previewUrl || imageFile ? (
                <div className={styles.uploadBox}>
                  <div className={styles.imagePreview}>
                    <img src={imageFile ? URL.createObjectURL(imageFile) : previewUrl} alt="Preview" />
                    <CloseRoundedIcon
                      className={styles.removeIcon}
                      onClick={() => {
                        setImageFile(null);
                        setPreviewUrl("");
                      }}
                    />
                  </div>

                  <div className={styles.uploadDetails}>
                    <Button className={styles.uploadButton} component="label">
                      <UploadIcon />
                      Upload New Image
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={(e) => {
                          const f = e.target.files && e.target.files[0];
                          if (f) {
                            setImageFile(f);
                          }
                        }}
                      />
                    </Button>
                    <h3>Choose image or Drop it here</h3>
                    <h4>JPG, JPEG, and PNG (Max size: 5 MB)</h4>
                  </div>
                </div>
              ) : (
                <div className={styles.uploadBox} style={{ flexDirection: "column", padding: "23px 0" }}>
                  <Button className={styles.uploadButton} component="label">
                    <UploadIcon />
                    Upload
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={(e) => {
                        const f = e.target.files && e.target.files[0];
                        if (f) setImageFile(f);
                      }}
                    />
                  </Button>
                  <h3>Choose image or Drop it here</h3>
                  <h4>JPG, JPEG, and PNG (Max size: 5 MB)</h4>
                </div>
              )}
            </div>
          </Grid>

          <Grid item xs={12} className={styles.textFieldArea} style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isAvailable}
                  onChange={(e) => setIsAvailable(e.target.checked)}
                  sx={{
                    color: "#CDCDCF",
                    "&.Mui-checked": {
                      color: "#e50218",
                    },
                  }}
                />
              }
              label={<span style={{ fontSize: "14px", color: "#2b2b2b", fontWeight: "500" }}>Available for sale</span>}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={hideFromPos}
                  onChange={(e) => setHideFromPos(e.target.checked)}
                  sx={{
                    color: "#CDCDCF",
                    "&.Mui-checked": {
                      color: "#e50218",
                    },
                  }}
                />
              }
              label={<span style={{ fontSize: "14px", color: "#2b2b2b", fontWeight: "500" }}>Hide from POS</span>}
            />
          </Grid>
        </Grid>

        <div className={styles.actionButtons}>
          <Button onClick={handleTestKOT} className={styles.buttonRed} style={{ marginRight: "auto" }}>
            Test KOT
          </Button>
          <div style={{ display: "flex", gap: "10px" }}>
            <Button onClick={onClose} className={styles.buttonGray}>
              Cancel
            </Button>
            <Button onClick={handleUpdate} className={styles.buttonRed}>
              Update
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EditCategoryPopup;
